import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { entityApi, pipelineNotificationApi } from '@/modules/pipelines/api';
import type { PipelineNode } from '@/modules/pipelines/api/entity-api';
import type { Notice } from '@/modules/pipelines/model/pipeline-alerts/pipeline-alert';
import { collectAssetKeys } from '@/modules/pipelines/data/pipelines';
import { derivePipelineMetrics } from '@/modules/pipelines/data/metrics';
import AssetMetrics from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-metrics';
import AssetEventTypeTable from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-event-type-table';
import AssetNoticeRanking from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-notice-ranking';
import NoticePlanningSplit from '@/modules/pipelines/components/deep-dive/asset-metrics/notice-planning-split';
import PipelineAssetTable from '@/modules/pipelines/components/deep-dive/asset-metrics/pipeline-asset-table';
import { Loading } from '@/shared/components/export';
import styles from '@/modules/pipelines/components/deep-dive/pipeline-map-tabs.module.scss';

const WINDOWS = [
  { days: 30, label: 'last 30 days' },
  { days: 90, label: 'last 90 days' },
  { days: 365, label: 'last 12 months' },
];

const ALL_PIPELINES = '';

export default function PipelineMapAssetMetricsPanel() {
  const [pipelineNodes, setPipelineNodes] = useState<PipelineNode[]>();
  const [notices, setNotices] = useState<Notice[]>();
  const [windowDays, setWindowDays] = useState(90);
  const [pipelineId, setPipelineId] = useState(ALL_PIPELINES);
  const [failed, setFailed] = useState(false);

  const windowLabel = WINDOWS.find(w => w.days === windowDays)?.label ?? `last ${windowDays} days`;

  useEffect(() => {
    let cancelled = false;

    entityApi
      .getPipelineGraph()
      .then(async nodes => {
        if (cancelled) return;
        setPipelineNodes(nodes);

        const assetKeys = collectAssetKeys(nodes);
        const sinceIso = dayjs().subtract(windowDays, 'day').toISOString();
        const found = await pipelineNotificationApi.getNoticesByEntities(assetKeys, sinceIso);
        if (!cancelled) setNotices(found);
      })
      .catch(err => {
        console.error('[asset-metrics] Failed to load pipeline metrics', err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const allMetrics = useMemo(
    () => (pipelineNodes ? derivePipelineMetrics(pipelineNodes, notices ?? []) : undefined),
    [pipelineNodes, notices],
  );

  const scopedMetrics = useMemo(() => {
    if (!pipelineNodes) return undefined;
    if (pipelineId === ALL_PIPELINES) return allMetrics;

    const selected = pipelineNodes.filter(node => node._id === pipelineId);
    return derivePipelineMetrics(selected, notices ?? []);
  }, [pipelineNodes, notices, pipelineId, allMetrics]);

  const pipelineOptions = useMemo(
    () =>
      (allMetrics?.pipelines ?? [])
        .map(row => ({ id: row.id, name: row.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allMetrics],
  );

  const loaded = !!pipelineNodes && !!notices && !!allMetrics && !!scopedMetrics;
  const scopeLabel =
    pipelineId === ALL_PIPELINES
      ? windowLabel
      : `${pipelineOptions.find(option => option.id === pipelineId)?.name ?? 'pipeline'} · ${windowLabel}`;

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.metricsScroll}>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Window</span>
            {WINDOWS.map(option => (
              <button
                key={option.days}
                type={'button'}
                className={`${styles.filterChip} ${
                  option.days === windowDays ? styles.filterChipActive : ''
                }`}
                aria-pressed={option.days === windowDays}
                onClick={() => setWindowDays(option.days)}
              >
                {option.label}
              </button>
            ))}

            <span className={styles.filterLabel}>Pipeline</span>
            <select
              className={styles.filterSelect}
              value={pipelineId}
              onChange={event => setPipelineId(event.target.value)}
              aria-label={'Filter the tiles and charts to one pipeline'}
            >
              <option value={ALL_PIPELINES}>All pipelines</option>
              {pipelineOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {failed && <div className={styles.error}>Couldn’t load the pipeline metrics.</div>}
          {!failed && !loaded && <Loading />}

          {!failed && loaded && (
            <>
              <div className={styles.metricsGrid}>
                <AssetNoticeRanking
                  assets={scopedMetrics.assetsByNoticeCount}
                  windowLabel={scopeLabel}
                />
                <NoticePlanningSplit planning={scopedMetrics.planning} windowLabel={scopeLabel} />
                <AssetMetrics summary={{ activeNotices: scopedMetrics.activeNotices }} />
                <AssetEventTypeTable
                  eventTypes={scopedMetrics.assetsByEventType}
                  windowLabel={scopeLabel}
                />
              </div>

              <PipelineAssetTable rows={allMetrics.pipelines} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
